import { queryClient, reactQueryUtils } from "@lib/react-query";
import { useMutation } from "@tanstack/react-query";
import { accountKeys } from "../account";
import { usersApi } from "./users.api";
import { usersKeys } from "./users.keys";
import type { TUser } from "./users.types";

export const useDeleteUserMutation = () => {
  return useMutation({
    mutationFn: (oktaId: string) => usersApi.deleteUser(oktaId),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...usersKeys.users]);
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useReactivateUserMutation = () => {
  return useMutation({
    mutationFn: (oktaId: string) => usersApi.reactivateUser(oktaId),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...usersKeys.users]);
    },
  });
};

export const useChangeRoleMutation = () => {
  return useMutation({
    mutationFn: ({ oktaId, newRole }: { oktaId: string; newRole: string }) =>
      usersApi.changeRole(oktaId, newRole),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...usersKeys.users]);
    },
  });
};

export const useUpdateUserMutation = () => {
  return useMutation({
    mutationFn: ({
      oktaId,
      userData,
    }: {
      oktaId: string;
      userData: { firstName: string };
    }) => usersApi.updateUser(oktaId, userData),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...usersKeys.users]);
    },
  });
};

export const useUpdateUserFirstShareMutation = () => {
  return useMutation({
    mutationFn: ({ id, orgId }: { id: string; orgId: string }) =>
      usersApi.updateUserFirstShare(id, orgId),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...usersKeys.currentUser]);
    },
  });
};

export const useUpdateUserCompleteProfileMutation = () => {
  return useMutation({
    mutationFn: ({ id, orgId }: { id: string; orgId: string }) =>
      usersApi.updateUserCompleteProfile(id, orgId),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...usersKeys.currentUser]);
    },
  });
};

export const useUpdateUserProfileMutation = () => {
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      usersApi.updateUserProfile(data),
    onSuccess: (user) => {
      reactQueryUtils.setQueryData([...usersKeys.currentUser], user);
    },
  });
};

export const useUpdateAutosaveCountMutation = () => {
  return useMutation({
    mutationFn: (counter: number) => usersApi.updateAutosaveCount(counter),
    onMutate: async (counter: number) => {
      await queryClient.cancelQueries({ queryKey: usersKeys.currentUser });
      const previousUser = reactQueryUtils.getQueryData<TUser>([
        ...usersKeys.currentUser,
      ]);

      if (previousUser) {
        const optimisticUser = {
          ...previousUser,
          autosaveCount: (previousUser.autosaveCount || 0) + counter,
        };
        reactQueryUtils.setQueryData(
          [...usersKeys.currentUser],
          optimisticUser,
        );
      }

      return { previousUser };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        reactQueryUtils.setQueryData(
          [...usersKeys.currentUser],
          context.previousUser,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...usersKeys.currentUser]);
    },
  });
};

export const useUpdateUserFirstViewEditorMutation = () => {
  return useMutation({
    mutationFn: () => usersApi.updateUserFirstViewEditor(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: usersKeys.currentUser });
      const previousUser = reactQueryUtils.getQueryData<TUser>([
        ...usersKeys.currentUser,
      ]);

      return { previousUser };
    },
    onSuccess: (data, _variables, context) => {
      if (context?.previousUser) {
        const updatedUser = {
          ...context.previousUser,
          firstViewEditor: (data as { firstViewEditor: boolean })
            .firstViewEditor,
        };
        reactQueryUtils.setQueryData([...usersKeys.currentUser], updatedUser);
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUser) {
        reactQueryUtils.setQueryData(
          [...usersKeys.currentUser],
          context.previousUser,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...usersKeys.currentUser]);
    },
  });
};

export const useUpdateUserFirstLoginMutation = () => {
  return useMutation({
    mutationFn: () => usersApi.updateFirstLogin(),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...usersKeys.currentUser]);
    },
  });
};
